import {
  AnchorButton,
  Button,
  Card,
  Collapse,
  ControlGroup,
  Elevation,
  Tag,
} from "@blueprintjs/core";
import React from "react";
import { apiCallPost, niceDate, yearFromNow } from "../fns/util";
import { inject, observer } from "mobx-react";
import cogoToast from "cogo-toast";
import _ from "lodash";
import { withRouter } from "react-router";
import AsyncSelWrap from "../utils/AsyncSelWrap";
import SingleIDSelectWrapper from "../utils/SingleIDSelectWrapper";
import { nanoid } from "nanoid";

class SxPatientData extends React.Component {
  state = {
    collapsed: true,
    /** PATIENT SEARCH STUFF */
    oldSearching: false,
    oldSearchResults: [],
    /** LOAD VISIT DETAILS */
    isLoadingVisits: false,
    fetchedVisits: [],
    hasFetchedVisits: false,
  };
  componentDidMount() {
    this.setState({ ...this.props.data });
  }
  searchPatients = async (string) => {
    if (!string) {
      this.setState({ oldSearchResults: [] });
      return;
    }
    try {
      this.props.onUpdate({
        patientName: "",
        patientGenericPid: "",
        patientPhone: "",
        patientAddress: "",
        patientSex: "",
        patientDob: "",
      });
      let res = await apiCallPost(
        `/rai/${this.props.authStore.prefix}/search`,
        {
          prefix: string,
        }
      );
      const normalizedQuery = (string || "").trim().toLowerCase();
      const exactPidMatches = res.filter(
        (each) => `${each.genericPid}`.toLowerCase() === normalizedQuery
      );
      const resultsToShow = exactPidMatches.length ? exactPidMatches : res;
      this.setState({
        oldSearchResults: resultsToShow.map((each) => {
          return {
            item: _.startCase(_.lowerCase(each.name)),
            entityTypeString: each.genericPid,
            helper: each.phone,
            helper2:
              new Date().getFullYear() - new Date(each.dob).getFullYear(),
            patientData: each,
          };
        }),
        oldSearching: false,
      });
    } catch (err) {
      cogoToast.error(
        "There was an error in searching. Please check the console for more details."
      );
    }
  };
  getLastVisit = async () => {
    try {
      this.setState({ isLoadingVisits: true });
      let resV = await apiCallPost(
        `/clinical2/${this.props.authStore.prefix}/getLastVisitEntitiesv2`,
        {
          genericPid: this.props.data.patientGenericPid,
        }
      );

      let res = resV.visits;
      let results = _.filter(
        res,
        (each) => each.entities && each.entities.length > 0
      );

      let visits = results.map((each) => {
        return {
          ...each,
          labelKey: `${niceDate(each.onDay)} - ${each.doctor} - ${each.branch}`,
        };
      });
      this.setState({
        isLoadingVisits: false,
        fetchedVisits: visits,
        hasFetchedVisits: true,
      });
    } catch (err) {
      console.log(err);
      this.setState({ isLoadingVisits: false });
      cogoToast.error(
        "There was an error in fetching the last visit. Please check the console for more details."
      );
    }
  };
  getEyeDx = async () => {
    try {
      let resV = await apiCallPost(
        `/booking-sx/${this.props.authStore.prefix}/getLastVisitEyeDx`,
        { genericPid: this.props.data.patientGenericPid }
      );
      let res = resV.visits;
      let idx = _.findIndex(
        res,
        (each) => each._id === this.props.data.referringVisitId
      );
      let dx = res[idx].visitEyeDx.map((each) => {
        return { ...each, nanoid: nanoid() };
      });
      this.props.onUpdate({ diagnoses: dx });
    } catch (err) {
      console.log(err);
      cogoToast.error(
        "There was an error in fetching the last visit. Please check the console for more details."
      );
    }
  };
  getAllergies = async () => {
    try {
      let res = await apiCallPost(
        `/booking-sx/${this.props.authStore.prefix}/getPtSysAllergies`,
        { genericPid: this.props.data.patientGenericPid }
      );
      let allergies = res.PtSysAllergies;
      this.props.onUpdate({ patientAllergies: allergies });
    } catch (err) {
      console.log(err);
      cogoToast.error(
        "There was an error in fetching allergies. Please check the console for more details."
      );
    }
  };
  getSystemicDiseases = async () => {
    try {
      let res = await apiCallPost(
        `/booking-sx/${this.props.authStore.prefix}/getPtSysDisease`,
        { genericPid: this.props.data.patientGenericPid }
      );
      let sys = res.PtSysDiseases;
      this.props.onUpdate({ patientSysDiseases: sys });
    } catch (err) {
      console.log(err);
      cogoToast.error(
        "There was an error in fetching systemic diseases. Please check the console for more details."
      );
    }
  };

  renderSysDiseases = () => {
    if (
      this.props.data.patientSysDiseases &&
      this.props.data.patientSysDiseases.length
    ) {
      let all = this.props.data.patientSysDiseases;
      return all.map((each) => {
        return (
          <ControlGroup>
            <Tag large intent="danger" icon="warning-sign">
              {each.entityName}
            </Tag>
          </ControlGroup>
        );
      });
    }
  };
  renderPatientData = () => {
    if (!this.props.data.patientGenericPid) {
      return null;
    }
    return (
      <ControlGroup vertical>
        <ControlGroup style={{ marginTop: "5px" }}>
          <Tag large minimal intent="primary">
            Patient Details
          </Tag>
          {this.props.data.lifecycle === "DRAFT" && (
            <Button
              disabled={this.props.isBusy}
              outlined
              icon="helper-management"
              text="Get Rx Details"
              loading={this.state.isLoadingVisits}
              onClick={() => {
                if (this.props.data.lifecycle === "DRAFT") {
                  this.getLastVisit();
                }
              }}
            />
          )}
          {this.props.data.lifecycle === "DRAFT" && (
            <AnchorButton
              disabled={this.props.isBusy}
              outlined
              icon="document-share"
              text="Edit Patient Details"
              href={`https://newchart.care/patientdetails/${this.props.data.patientGenericPid}`}
              target="_blank"
            />
          )}
        </ControlGroup>
        <ControlGroup style={{ marginTop: "5px" }}>
          <Tag large minimal intent="primary">
            Name
          </Tag>
          <Tag large intent="primary">
            {this.props.data.patientName}
          </Tag>
        </ControlGroup>
        <ControlGroup style={{ marginTop: "5px" }}>
          <Tag large minimal intent="primary">
            PID
          </Tag>
          <Tag large intent="primary">
            {this.props.data.patientGenericPid}
          </Tag>
        </ControlGroup>
        <ControlGroup style={{ marginTop: "5px" }}>
          <Tag large minimal intent="primary">
            Sex
          </Tag>
          <Tag large intent="primary">
            {this.props.data.patientSex}
          </Tag>
          <Tag large minimal intent="primary">
            Age
          </Tag>
          <Tag large intent="primary">
            {new Date().getFullYear() -
              new Date(this.props.data.patientDob).getFullYear()}
          </Tag>
        </ControlGroup>
        <ControlGroup style={{ marginTop: "5px" }}>
          <Tag large minimal intent="primary">
            Phone
          </Tag>
          <Tag large intent="primary">
            {this.props.data.patientPhone}
          </Tag>
        </ControlGroup>
        <ControlGroup style={{ marginTop: "5px" }}>
          <Tag large minimal intent="primary">
            Address
          </Tag>
          <Tag large intent="primary" multiline>
            {this.props.data.patientAddress}
          </Tag>
        </ControlGroup>
        {this.props.data.patientAllergies &&
        this.props.data.patientAllergies.length ? (
          <>
            <ControlGroup style={{ marginTop: "5px" }}>
              <ControlGroup>
                <Tag intent="danger" large>
                  Allergies
                </Tag>
              </ControlGroup>
              <ControlGroup vertical>{this.renderAllergies()}</ControlGroup>
            </ControlGroup>
          </>
        ) : null}
        {this.props.data.patientSysDiseases &&
        this.props.data.patientSysDiseases.length ? (
          <>
            <ControlGroup style={{ marginTop: "5px" }}>
              <ControlGroup>
                <Tag intent="danger" large>
                  Pre Dx
                </Tag>
              </ControlGroup>
              <ControlGroup vertical>{this.renderSysDiseases()}</ControlGroup>
            </ControlGroup>
          </>
        ) : null}
      </ControlGroup>
    );
  };
  renderPatientSearch = () => {
    if (this.props.data.lifecycle === "DRAFT") {
      return (
        <div>
          <ControlGroup>
            <Tag large minimal>
              Search Patients
            </Tag>
            <AsyncSelWrap
              disabled={this.props.isBusy}
              autoFocus={true}
              placeholder="Patients..."
              searching={this.state.oldSearching}
              items={_.take(this.state.oldSearchResults, 10)}
              selectedItem={null}
              update={(item) => this.searchPatients(item)}
              onChange={async (item) => {
                let obj;
                try {
                  let res = await apiCallPost(
                    `/clinical2/${this.props.authStore.prefix}/extra/getPatientInfo`,
                    {
                      patientId: item.patientData.genericPid,
                    }
                  );

                  obj = {
                    patientName: res.name,
                    patientGenericPid: res.sysPid,
                    patientPhone: res.phone,
                    patientAddress: res.address,
                    patientSex: res.sex,
                    patientDob: res.dob,
                  };
                } catch (err) {
                  cogoToast.error(
                    "There was an error in searching. Please check the console for more details."
                  );
                }

                this.props.onUpdate(obj);
                this.setState({
                  fetchedVisits: [],
                  referringSurgeonId: null,
                  hasFetchedVisits: false,
                  // selectedVisit: null,
                  referringSurgeonName: null,
                  referringVisitId: null,
                  sxAdvised: [],
                  pastAllergies: [],
                  pastDiseases: [],
                  pastProcedures: [],
                });
                this.renderVisitSearch();
              }}
            />
          </ControlGroup>
        </div>
      );
    }
  };
  renderVisitSearch = () => {
    if (!this.props.data.patientGenericPid) {
      return null;
    }
    if (this.state.isLoadingVisits) {
      return null;
    }
    if (!this.state.hasFetchedVisits && !this.props.data.referringSurgeonId) {
      return null;
    }
    if (
      !this.state.fetchedVisits.length &&
      !this.props.data.referringSurgeonId
    ) {
      return (
        <Tag minimal large>
          No relevant visits.
        </Tag>
      );
    }
    if (this.props.data.referringSurgeonId) {
      return (
        <ControlGroup vertical style={{ marginTop: "5px" }}>
          <ControlGroup>
            <Tag intent="warning" large minimal>
              Referral Details
            </Tag>
            {this.props.data.lifecycle === "DRAFT" ? (
              <Button
                outlined
                text="Edit"
                icon="edit"
                onClick={() => {
                  this.props.onUpdate({
                    referringSurgeonId: null,
                    referringSurgeonName: null,
                    referringVisitId: null,
                    sxAdvised: [],
                    pastAllergies: [],
                    pastDiseases: [],
                    pastProcedures: [],
                  });
                }}
              />
            ) : null}
          </ControlGroup>
          <ControlGroup style={{ marginTop: "5px" }}>
            <Tag intent="warning" large minimal>
              Referring Doctor
            </Tag>
            <Tag intent="warning" large>
              {this.props.data.referringSurgeonName}
            </Tag>
          </ControlGroup>
          <ControlGroup style={{ marginTop: "5px" }}>
            <Tag intent="warning" large minimal>
              Visit Details
            </Tag>
            <Tag intent="warning" large>
              {niceDate(this.props.data.selectedVisit?.onDay)} -{" "}
              {this.props.data.selectedVisit?.branch}
            </Tag>
            <AnchorButton
              outlined
              text="View Prescription"
              target="_blank"
              icon="document-share"
              href={`https://print.newchart.care/rx2/${this.props.data.referringVisitId}`}
            />
          </ControlGroup>
          {this.renderAdvisedSx(this.props.data.sxAdvised)}
          {this.renderPastDiseases(this.props.data.pastDiseases)}
          {this.renderPastProcedures(this.props.data.pastProcedures)}
          {this.renderAllergies(this.props.data.pastAllergies)}
        </ControlGroup>
      );
    }

    if (this.props.data.lifecycle === "DRAFT") {
      return (
        <div style={{ marginTop: "5px" }}>
          <ControlGroup>
            <Tag minimal large>
              Choose Visit
            </Tag>
            <SingleIDSelectWrapper
              showSelectProp={true}
              width="300px"
              items={this.state.fetchedVisits}
              selectedItem={this.props.data.selectedVisit}
              labelKey="labelKey"
              onChange={(item) => {
                let obj = {
                  referringSurgeonId: item.doctorId,
                  referringSurgeonName: item.doctor,
                  referringVisitId: item._id,
                  sxAdvised: item.entities.filter(
                    (entity) =>
                      entity.sectionName === "PLAN" &&
                      entity.subsectionName === "SURGERY"
                  ),
                  pastDiseases: item.entities.filter(
                    (entity) =>
                      entity.sectionName === "HISTORY" &&
                      entity.subsectionName === "PAST_DISEASE"
                  ),
                  pastAllergies: item.entities.filter(
                    (entity) =>
                      entity.sectionName === "HISTORY" &&
                      entity.subsectionName === "ALLERGY"
                  ),
                  pastProcedures: item.entities.filter(
                    (entity) =>
                      entity.sectionName === "HISTORY" &&
                      entity.subsectionName === "PAST_PROCEDURE"
                  ),
                };
                this.props.onUpdate({ selectedVisit: item });
                this.props.onUpdate(obj);
              }}
            />
          </ControlGroup>
        </div>
      );
    }
  };
  renderAdvisedSx = (sx) => {
    return sx.map((each) => {
      return (
        <ControlGroup style={{ marginTop: "5px", width: "34vw" }}>
          {" "}
          <Tag multiline intent="warning" large minimal>
            Advised Sx:
          </Tag>
          <Tag multiline intent="warning" large style={{ width: "27vw" }}>
            {each.laterality} {each.entityName} {each.notes}
          </Tag>
        </ControlGroup>
      );
    });
  };
  renderPastDiseases = (sx) => {
    if (this.props.data.pastAllergies && this.props.data.pastAllergies.length) {
      return sx.map((each) => {
        return (
          <ControlGroup style={{ marginTop: "5px", width: "34vw" }}>
            {" "}
            <Tag multiline intent="warning" large minimal>
              Diseases:
            </Tag>
            <Tag multiline intent="warning" large style={{ width: "27vw" }}>
              {each.laterality} {each.entityName} {each.notes}
            </Tag>
          </ControlGroup>
        );
      });
    }
  };
  renderPastProcedures = (sx) => {
    if (
      this.props.data.pastProcedures &&
      this.props.data.pastProcedures.length
    ) {
      return sx.map((each) => {
        return (
          <ControlGroup style={{ marginTop: "5px", width: "34vw" }}>
            {" "}
            <Tag multiline intent="warning" large minimal>
              Previous procedures:
            </Tag>
            <Tag multiline intent="warning" large style={{ width: "27vw" }}>
              {each.laterality} {each.entityName} {each.notes}
            </Tag>
          </ControlGroup>
        );
      });
    }
  };
  renderAllergies = (sx) => {
    if (this.props.data.pastAllergies && this.props.data.pastAllergies.length) {
      return sx.map((each) => {
        return (
          <ControlGroup style={{ marginTop: "5px", width: "34vw" }}>
            {" "}
            <Tag multiline intent="warning" large minimal>
              Allergies:
            </Tag>
            <Tag multiline intent="warning" large style={{ width: "27vw" }}>
              {each.laterality} {each.entityName} {each.notes}
            </Tag>
          </ControlGroup>
        );
      });
    }
  };
  renderBasedOnLifecycle = () => {
    return (
      <>
        <Collapse isOpen={this.state.collapsed}>
          {" "}
          <Card elevation={Elevation.FOUR}>
            <Tag large intent="primary" style={{ marginBottom: "10px" }}>
              Patient Data
            </Tag>
            <Tag large minimal>
              {this.props.data.patientName} -{" "}
              {this.props.data.patientGenericPid}
            </Tag>
            <Tag large minimal>
              {this.props.data.patientSex}/
              {new Date().getFullYear() -
                new Date(this.props.data.patientDob).getFullYear()}
            </Tag>
            {this.props.data.patientAllergies &&
            this.props.data.patientAllergies.length ? (
              <Tag intent="danger" large>
                Allergies:{" "}
                {this.props.data.patientAllergies
                  .map((each) => each.allergy)
                  .join(", ")}
              </Tag>
            ) : null}
            {this.props.data.patientSysDiseases &&
            this.props.data.patientSysDiseases.length ? (
              <Tag intent="warning" large>
                Systemic Diseases:{" "}
                {this.props.data.patientSysDiseases
                  .map((each) => each.disease)
                  .join(", ")}
              </Tag>
            ) : null}

            <Button
              icon="double-chevron-down"
              outlined
              onClick={() => this.setState({ collapsed: false })}
            />
          </Card>
        </Collapse>{" "}
        <Collapse isOpen={!this.state.collapsed}>
          <Card elevation={Elevation.FOUR} className="dark__bg">
            <ControlGroup style={{ marginBottom: "10px" }}>
              <Tag large fill intent="primary">
                Patient Data
              </Tag>
              <Button
                icon="double-chevron-down"
                outlined
                onClick={() => this.setState({ collapsed: true })}
              />
            </ControlGroup>

            <br />
            <div
              style={
                this.props.canEdit
                  ? {}
                  : {
                      "pointer-events": "none",
                      opacity: 0.5,
                    }
              }
            >
              {this.renderPatientSearch()}
              {this.renderPatientData()}
              {this.renderVisitSearch()}
            </div>
          </Card>
        </Collapse>
      </>
    );
  };
  render() {
    return <div>{this.renderBasedOnLifecycle()}</div>;
  }
}

export default inject("authStore")(observer(withRouter(SxPatientData)));
